// Share links (community only — enterprise has no sharing, decided 2026-09-15).
//
//   COPY / RESET   — an admin or editor of the item, on the main API.
//   REDEEM         — anybody holding a code: what it points at, and the cookie.
//   READ           — a link holder, under /s/<shareId> (community/share).
//
// A link is `/s/col/<code>` or `/s/stu/<code>`, optionally followed by short
// suffixes naming the study and slide inside it. Only the 16-character code is
// a secret; the kind segment and the suffixes are labels, and the server never
// trusts them.
import { getConfig } from '../../config/runtimeConfig';

export type ShareKind = 'collection' | 'study';
/** link: opens here without an account. public: needs an account. private: paused. */
export type ShareAccess = 'link' | 'public' | 'private';

export interface Redeemed {
  access: ShareAccess;
  kind: ShareKind;
  /** The `col_` or `stu_` public id. Not a secret: an id grants nothing. */
  targetId: string;
  /** Only when access is 'link': the namespace the cookie opens. */
  shareId?: string;
}

export interface ShareLink {
  kind: ShareKind;
  code: string;
  /** `/s/col/<code>`: an app path, before the bundle's base. */
  path: string;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${getConfig().apiRoot}${path}`, {
      credentials: 'include',
      headers: init?.body ? { 'content-type': 'application/json' } : {},
      ...init,
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.', 0);
  }
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new ApiError(body.error || `Request failed (${res.status})`, res.status);
  return body as T;
}

export const SEGMENT: Record<ShareKind, string> = { collection: 'col', study: 'stu' };
export const kindOfSegment = (s: string | undefined): ShareKind | null => (s === 'col' ? 'collection' : s === 'stu' ? 'study' : null);
export const CODE_RE = /^[0-9A-Za-z]{16}$/;

/** The item's link: the live one, or a new one the first time. Shared or Public items only. */
export const getShareLink = (kind: ShareKind, id: string): Promise<ShareLink> =>
  call('/shares/link', { method: 'POST', body: JSON.stringify({ kind, id }) });

/** A new link for the item; the old one stops working. */
export const resetShareLink = (kind: ShareKind, id: string): Promise<ShareLink> =>
  call('/shares/link/reset', { method: 'POST', body: JSON.stringify({ kind, id }) });

/** What a code points at; sets the share cookie when the item is Shared. */
export const redeemShare = (code: string): Promise<Redeemed> =>
  call('/shares/redeem', { method: 'POST', body: JSON.stringify({ code }) });

/** A GET inside a share's namespace; null for "not in this link" (404). */
export async function shareGet<T>(shareId: string, path: string): Promise<T | null> {
  try {
    return await call<T>(`/s/${encodeURIComponent(shareId)}${path}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/** The in-app path of a link (the router adds the bundle's base). */
export function sharePath(kind: ShareKind, code: string, ...suffixes: Array<string | undefined>): string {
  return `/s/${SEGMENT[kind]}/${code}${suffixes.filter(Boolean).map((s) => `/${s}`).join('')}`;
}

/** The URL to hand someone. */
export function shareUrl(path: string, ...suffixes: Array<string | undefined>): string {
  const base = import.meta.env.BASE_URL.replace(/\/+$/, '');
  return `${window.location.origin}${base}${path}${suffixes.filter(Boolean).map((s) => `/${s}`).join('')}`;
}

const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

/**
 * The short, NON-SECRET name of a study or slide inside a link: six base62
 * characters of the FNV-1a 64-bit hash of its id — a study's public id
 * (`stu_…`), a slide's SeriesInstanceUID.
 *
 * Only ever matched against the ids a link already reaches (the slides of its
 * study, the studies of its collection), so it chooses what opens and can
 * reach nothing more. Collisions among a few hundred ids are ~1 in 10^5;
 * the first match wins, and a miss opens the default.
 */
export function uidSuffix(uid: string): string {
  const mask = (1n << 64n) - 1n;
  let h = 0xcbf29ce484222325n;
  for (const b of new TextEncoder().encode(uid)) {
    h ^= BigInt(b);
    h = (h * 0x100000001b3n) & mask;
  }
  let n = h % 56800235584n; // 62^6
  let out = '';
  for (let i = 0; i < 6; i++) {
    out = ALPHABET[Number(n % 62n)] + out;
    n /= 62n;
  }
  return out;
}
